<script setup lang="ts">
import { ObjectType } from "@/models/objects/types";
import * as cesiumCommon from "@/utils/cesium-common";
import { errorCauseCode, errorMessage } from "@/utils/errorUtils";
import { notifyError, notifySuccess } from "@/utils/notificationUtils.js";
import validationRules from "@/utils/validation-rules.js";

const rules = {
	...validationRules,
	required: (value) => !!value || "*",
	required0: (value) => value === 0 || !!value || "*",
	fgValidInterpolation: (v) => Number.parseFloat(v) > 0,
};
</script>

<template>
  <v-card-text class="object-list-item">
    <v-card-actions class="py-0" :style="isEditOnProgress ? 'background-color: #0064BA;' : ''">
      <!-- 表示ステータス切替 -->
      <v-btn variant="text" icon size="24" @click="toggleVisibility" :disabled="isDisabledToggleVisibilityBtn">
        <v-icon>
          {{ obj.isShow ? "icon:View" : "icon:View-Off" }}
        </v-icon>
      </v-btn>
      <!-- パネル開閉 -->
      <v-btn variant="text" icon size="28" @click="obj.isOpen = !obj.isOpen" :disabled="isEditOnProgress" class="ml-0">
        <v-icon> mdi-play {{ obj.isOpen && !isEditOnProgress ? "mdi-rotate-90" : "" }} </v-icon>
      </v-btn>
      <!-- オブジェクト名 -->
      <div class="d-flex obj-name-area">
        <img v-if="obj.type === ObjectType.ROAD" class="mr-1" :src="TemporaryRoadIcon" alt="Temporary Road Icon" />
        <img v-if="obj.type === ObjectType.FLAT_GROUND" class="mr-1" :src="FlatFieldIcon" alt="Temporary Road Icon" />
        <img v-if="obj.type === ObjectType.TRENCH" class="mr-1" :src="DiggingIcon" alt="Temporary Road Icon" />
        <object-name :objId="obj.id">
        </object-name>
      </div>
      <!-- サーチボタン -->
      <v-btn variant="text" @click="fitToView" size="24" :disabled="!obj.isShow" icon>
        <v-icon size="small">icon:Search</v-icon>
      </v-btn>
      <!-- メニューボタン -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn variant="text" size="24" icon v-bind="props">
            <v-icon> icon:Overflow-Menu-Vertical </v-icon>
          </v-btn>
        </template>
        <v-list density="compact">
          <!-- 出力 -->
          <v-list-item :disabled="!obj.isLoaded" @click="exportByJson">
            <v-icon class="mr-4">icon:Export</v-icon>
            <span class="menu-item-label">{{ $t("EXPORT_AS") }}</span>
          </v-list-item>
          <!-- 中心線形出力 -->
          <v-list-item v-if="obj.type === ObjectType.ROAD || obj.type === ObjectType.TRENCH"
            :disabled="isCenterLineExportDisabled" @click="exportCenterlineForObject">
            <v-icon class="mr-4">icon:Export</v-icon>
            <span class="menu-item-label">{{ $t("EXPORT_CENTERLINE") }}</span>
          </v-list-item>
          <!-- 断面図出力 -->
          <v-list-item v-if="obj.type === ObjectType.ROAD || obj.type === ObjectType.TRENCH"
            :disabled="isCrossSectionExportDisabled" @click="exportCrossSectionForObject">
            <v-icon class="mr-4">icon:Export</v-icon>
            <span class="menu-item-label">{{ $t("EXPORT_CROSS_SECTION_DXF") }}</span>
          </v-list-item>
          <!-- 編集 -->
          <v-list-item @click="toggleEdit" :disabled="isEditOnProgress">
            <v-icon class="mr-4">icon:Pencil</v-icon>
            <span class="menu-item-label">{{ $t("EDIT") }}</span>
          </v-list-item>
          <!-- 複製 -->
          <v-list-item @click="duplicateObj" :disabled="isEditOnProgress">
            <v-icon class="mr-4">mdi-content-copy</v-icon>
            <span class="menu-item-label">{{ $t("DUPLICATE") }}</span>
          </v-list-item>
          <!-- 削除 -->
          <v-list-item @click="deleteObj">
            <v-icon class="mr-4">icon:Trash-Can</v-icon>
            <span class="menu-item-label">{{ $t("REMOVE") }}</span>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-actions>
    <div v-if="!isEditOnProgress && obj.type === ObjectType.ROAD">
      <road-panel :objId="obj.id" :rules="rules"> </road-panel>
    </div>
    <div v-else-if="!isEditOnProgress && obj.type === ObjectType.FLAT_GROUND">
      <flat-ground-panel :objId="obj.id" :rules="rules"></flat-ground-panel>
    </div>
    <div v-else-if="!isEditOnProgress && obj.type === ObjectType.TRENCH">
      <trench-panel :objId="obj.id" :rules="rules"></trench-panel>
    </div>
  </v-card-text>

</template>

<script lang="ts">
import FlatGroundPanel from "./ObjectPanels/FlatGroundPanel.vue";
import RoadPanel from "./ObjectPanels/RoadPanel.vue";
import TrenchPanel from "./ObjectPanels/TrenchPanel.vue";
import ObjectName from "./ObjectName.vue";
import * as constants from "@/constant.js";
import axios from "axios";
import { touchSiteV2 } from "@/api/startupApi";
import { selectByMigration } from "@/config/v2Migration";
import { mapMutations, mapActions, useStore } from "vuex";
import * as lockUtils from "@/utils/lock-utils";
import dayjs from "dayjs";
import TemporaryRoadIcon from "@/assets/icons/customIcons/temporary-road-icon.svg";
import FlatFieldIcon from "@/assets/icons/customIcons/flat-field-icon.svg";
import DiggingIcon from "@/assets/icons/customIcons/digging-icon.svg";
import objUpdateMixin from '@/mixins/objUpdateMixin.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter)
import Slope2dValueInput from "../Slope2dValueInput.vue";
import {
  addClickTypeLog,
  GoogleAnalyticsClickTypes,
} from "../../utils/googleAnalytics.js";


export default {
  mixins: [objUpdateMixin], //obj comes from mixin
  components: {
    FlatGroundPanel,
    RoadPanel,
    TrenchPanel,
    ObjectName,
    Slope2dValueInput
  },
  props: {
    objId: String,
  },
  data() {
    return {
      store: useStore(),
      constants,
      TemporaryRoadIcon,
      FlatFieldIcon,
      DiggingIcon,
      isDisabledToggleVisibilityBtn: false
    };
  },
  computed: {
    lockInfo() {
      return this.store.state.lock.lockInfo;
    },
    isObjectUpdating() {
      return this.store.state.isObjectUpdating;
    },
    isEditOnProgress() {
      return this.store.state.object.objectUnderEdit?.id === this.obj.id;
    },
    isCenterLineExportDisabled() {
      return !this.obj.isVisible.centerLine;
    },
    isCrossSectionExportDisabled() {
      return !this.obj.isVisible.crossSection;
    }
  },
  methods: {
    ...mapMutations(["set_snackbar"]),
    ...mapActions("object", ["startEdit", "endEdit"]),
    waitForUpdate() {
      // アップロードが完了するまでステータスを監視して待機
      return new Promise((resolve, reject) => {
        if (!this.isObjectUpdating) {
          resolve(true);
        } else {
          const unwatch = this.store.watch(
            (state) => state.isObjectUpdating,
            (newValue) => {
              if (!newValue) {
                unwatch();
                resolve(true);
              }
            }
          );
        }
      })
    },
    async toggleVisibility() {
      if (this.obj.isShow) {
        // 連打防止のためアップロード中はボタン非活性にする
        this.isDisabledToggleVisibilityBtn = true;
        await this.waitForUpdate()
        this.isDisabledToggleVisibilityBtn = false;
      }
      const isShow = !this.obj.isShow;
      try {
        if (isShow) {
          if (!this.obj.associatedTerrain?.id) {
            throw new Error("No associated terrain");
          }
          const associatedTerrain = this.store.getters.terrainList.find((p) => p.getAssetId() == this.obj.associatedTerrain.id);
          if (!associatedTerrain) {
            throw new Error("Associated terrain not found");
          }
          await cesiumCommon.setAssociatedTerrainVisible(associatedTerrain);
        }
        await this.store.dispatch("executeWithSpinner", async () => {
          await this.obj.setVisibility(isShow);
        });

      } catch (e: unknown) {
        // エラーが発生したら表示状態を元に戻す
        console.error(e);
        this.obj.isShow = !this.obj.isShow;
        throw e;
      }
    },
    async toggleEdit() {
      if (this.isEditOnProgress) {
        return new Promise((resolve, reject) => {
          this.store.dispatch("executeWithSpinner", async () => {
            try {
              await this.endEdit();
              this.store.dispatch("inProgressModeManager/endEdit");
              resolve(true);
            } catch (error: unknown) {
              console.error(error);
              reject(error);
            }
          });
        });
      } else {
        try {
          addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EDIT);
          // オブジェクト作成中の場合キャンセル

          // await this.store.dispatch("inProgressModeManager/startEdit");

          if (!this.obj.isShow) {
            await this.toggleVisibility(this.obj);
          }
          // Need to clear altitude view and section view by default
          // Reason: there are few cases where altitude view and section view is not clear when flat ground edit called.
          this.clear2DViews();
          await this.startEdit(this.obj);
          // this.store.dispatch("lock/keepLock");
          // this.store.dispatch("startEditMode", this.obj.id)

        } catch (error: unknown) {
          if (errorCauseCode(error) === "CANCELLED_BY_USER") {
            await this.endEdit();
            this.store.dispatch("inProgressModeManager/endEdit");
          }
          console.error(error);
        }
      }
    },
    async deleteObj() {
      // Google Analyticsに連携
      addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_DELETE);

      try {
        const siteId = this.$route.query.siteId;
        // 他人がロックしている場合エラー
        const lockedObject = await lockUtils.checkLockStatus(siteId, this.obj.id);
        if (lockedObject.locked_by) {
          notifyError({ $store: this.store, $t: this.$t }, this.$t("EXCLUSIVE_NOTICE", { lockUser: lockedObject.locked_by }));
          return;
        }
        if (this.isEditOnProgress) {
          this.store.commit("object/clearObjectUnderEdit");
          this.store.commit("inProgressModeManager/clearInProgressMode");
          lockUtils.releaseLock();
        }
        // 削除処理
        this.obj.deleteObject();
        await this.updateMeasurementView();
        await this.store.dispatch("object/deleteObject", { siteId, obj: this.obj });
      } catch (error: unknown) {
        console.error(error);
        notifyError(
          { $store: this.store, $t: this.$t },
          `${this.obj.getName()} ${this.$t("REMOVE")} ${this.$t("failed")}`,
        );
      }
    },
    async duplicateObj() {
      try {
        const siteId = this.$route.query.siteId;
        await this.$store.dispatch("object/duplicateObject", {
          siteId,
          obj: this.obj,
        });
        notifySuccess({ $store: this.store, $t: this.$t }, this.$t("DUPLICATION_SUCCESSFUL"));
      } catch (e: unknown) {
        console.error(e);
        const message = errorMessage(e);
        if (message.startsWith("LOCKED:")) {
          const lockUser = message.replace("LOCKED:", "");
          notifyError({ $store: this.store, $t: this.$t }, this.$t("EXCLUSIVE_NOTICE", { lockUser }));
        } else {
          notifyError({ $store: this.store, $t: this.$t }, this.$t("DUPLICATION_FAILED"));
        }
      }
    },
    async updateMeasurementView() {
      for (let measurement of this.store.state.measurement.measurementList) {

        if (measurement.isCrossSectionVisible) {
          await window["viewer"].updateMeasurement(String(measurement.measurementId), "crossSection");
        }

        if (measurement.isLinearVisible) {
          await window["viewer"].updateMeasurement(String(measurement.measurementId), "linear");
        }
      }
    },
    async fitToView() {
      try {
        this.clear2DViews();
        this.obj.fitToView();
        this.updateObject2DViewVisibility(true, this.obj.id);
      } catch (err: unknown) {
        console.error(err);
      }
    },
    exportByJson() {
      const geometry = this.obj.getGeometry();
      const event = new CustomEvent("openPropertyDialog", {
        detail: { type: 6, name: this.obj.name, geometry: geometry, obj: this.obj, roadObjForExport: this.obj },
      });
      window.dispatchEvent(event);
    },
    exportCenterlineForObject() {      
      const customEvent = new CustomEvent("openExportLineworksFormatDialog", {
        detail: { entity: this.obj },
      });
      window.dispatchEvent(customEvent);
    },
    async exportCrossSectionForObject() {
      await this.store.dispatch("executeWithSpinner", async () => {
        try {
          await this.store.dispatch(
            "lineWorksCrossSection/exportCrossSection",
            this.obj,
          );
        } catch (e: unknown) {
          console.error(e);
          notifyError(
            { $store: this.store, $t: this.$t },
            this.$t("LINEWORKS_CROSS_SECTION_DOWNLOAD_FAILED", {
              name: this.obj.name,
            }),
          );
        }
      });
    },
    async exclusiveCheck() {
      let editSiteData = await this.getSiteInfo();

      const isUpdated = dayjs(this.store.state.initial_site_data.updated_at).isBefore(editSiteData.updated_at);

      // 現場が更新されていなければ排他制御を行わない
      if (!isUpdated) return false;

      // 自分以外が現場を更新していれば排他制御を行う
      return editSiteData.updated_by !== this.store.state.user.id;
    },
    updateGlobeVisibility(visibility) {
      this.$emit("updateGlobeVisibility", visibility);
    },
    updateObject2DViewVisibility(show2DView, id) {
      this.$emit("updateObject2DViewVisibility", show2DView, id);
    },
    clear2DViews() {
      this.$emit("clear2DViews");
    },
    async getSiteInfo() {
      const res = await axios.get(`${import.meta.env.VITE_API_BASE}/sites/${this.$route.query.siteId}`, {
        auth: this.store.state.authObject
      });
      return res ? res.data : null;
    },
    async updateSiteInfo() {
      await selectByMigration("startup", {
        v1: () =>
          axios.put(`${import.meta.env.VITE_API_BASE}/sites/touch/${this.$route.query.siteId}`, null, {
            auth: this.store.state.authObject
          }),
        v2: () => touchSiteV2(String(this.$route.query.siteId)),
      })();
    },
  },
};
</script>

<style scoped>
.list-item {
  height: 28px;
  align-items: center;
  gap: 4px;
}

.object-list-item {
  padding: 0;
  padding-left: 32px;
}

.obj-name-area {
  flex: 1;
  min-width: 0;
}

.menu-item-label {
  font-size: 13px;
}
</style>
